import type { FormError } from '@nuxt/ui';
import type { ICredentialsData } from '~/store/sign-up';
import { rules } from '#shared/validation';
import { usePasswordValidation } from '~/composables/validation/usePasswordValidation';

export function useSignUpValidation(credentials: Ref<ICredentialsData>) {
  const validationErrorsMap = ref(new Map());
  const checkedFreeNames = new Set<string>([]);
  const checkedTakenNames = new Set<string>([]);
  const checkedFreeEmails = new Set<string>([]);
  const checkedTakenEmails = new Set<string>([]);

  const { isPasswordValid } = usePasswordValidation(() => credentials.value.password);

  async function checkEmailAvailable(emailToTest?: string) {
    const email = emailToTest || credentials.value.email.trim();

    if (checkedFreeEmails.has(email)) {
      return true;
    } else if (checkedTakenEmails.has(email)) {
      return false;
    } else {
      const isEmailAvailable = await stores.user.checkEmailAvailable(email);

      if (isEmailAvailable) checkedFreeEmails.add(email);
      else checkedTakenEmails.add(email);

      return isEmailAvailable;
    }
  }

  async function checkUserNameAvailable() {
    const name = credentials.value.username.trim();

    if (checkedFreeNames.has(name)) {
      return true;
    } else if (checkedTakenNames.has(name)) {
      return false;
    } else {
      const isNameAvailable = await stores.user.checkUserNameAvailable(name);

      if (isNameAvailable) checkedFreeNames.add(name);
      else checkedTakenNames.add(name);

      return isNameAvailable;
    }
  }

  async function validate(state: any): Promise<FormError[]> {
    const errors = [];

    const testUsername = rules.username.safeParse(state.username);
    if (testUsername.error) {
      errors.push({ name: 'username', message: testUsername.error.issues[0]?.message as string });
    } else if (!(await checkUserNameAvailable())) {
      errors.push({ name: 'username', message: 'This username is already taken' });
    }

    const testEmail = rules.email.safeParse(state.email);
    if (testEmail.error) {
      errors.push({ name: 'email', message: testEmail.error.issues[0]?.message as string });
    } else if (!(await checkEmailAvailable())) {
      errors.push({ name: 'email', message: 'This email is already taken' });
    }

    if (!isPasswordValid.value) {
      errors.push({ name: 'password', message: 'Password is not valid' });
    }

    const testIsAdult = rules.isAdult.safeParse(state.isAdult);

    if (testIsAdult.error) {
      errors.push({ name: 'isAdult', message: 'This field is required' });
    }

    const testTerms = rules.isAgreeWithTnc.safeParse(state.isAgreeWithTnc);
    if (testTerms.error) {
      errors.push({ name: 'isAgreeWithTnc', message: 'This field is required' });
    }

    validationErrorsMap.value = new Map(errors.map((item) => [item.name, true]));
    return errors;
  }

  async function validateField(field: string) {
    const errors = await validate(credentials.value);
    const fieldError = errors.find((error) => error.name === field);
    return !!fieldError;
  }

  return {
    validate,
    validateField,
    checkEmailAvailable,
    validationErrorsMap,
  };
}
